import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { AxiosError } from "axios";
import { ThalbhetHistoryData } from "~/interface/thalbhet/thalbhet";
import API_PATH from "~/utils/api-routes";
import { createSmkApi, setLatestOtpToken } from "~/utils/smkInterseptor";
import { SmkApiService } from "~/utils/smkService";
import fileSaver from "file-saver";
import { getCookie } from "~/utils/cookies";
import { AUTH_COOKIE_TOKEN } from "~/utils/constant";

type paymentStatusType = "success" | "failed" | "cancelled" | null;

interface ThalBhetState {
  currentStep: number;
  totalSteps: number;
  basicInfo: {
    thalForWhat: "family_member" | "outer_family" | string; // "family_member" or "outer_family"
    familyMember: string;
    mobileNumber: string;
    currentVillage?: string; // Optional field for current village
    countryPhoneCode?: string; // Optional field for country code
  };
  rasoiInfo: {
    selectedNimit: string;
    nimitDetails?: string | null;
    date: Date | string;
    needPrasad: boolean | null; // Use boolean or null to handle the toggle state
  };
  paymentInfo: {
    // Add payment related fields here when needed
  };
  loading: boolean;
  error: string | null;
  paymentResponseDialogOpen?: boolean; // Optional field to manage payment response dialog state
  paymentAccessKey: string | null;
  paymentStatus: paymentStatusType;
  transactionId: string | null; // Initialize transaction ID
  ThalBhetHistory: ThalbhetHistoryData[]; // Optional field for Thalbhet history
  isRequiredPanCard: boolean; // Optional field to indicate if PAN card is required
  thalbhetId: number | null; // Optional field for Thalbhet ID
}

const initialState: ThalBhetState = {
  currentStep: 1,
  totalSteps: 3,
  basicInfo: {
    thalForWhat: "",
    familyMember: "",
    mobileNumber: "",
    currentVillage: "",
  },
  rasoiInfo: {
    selectedNimit: "",
    nimitDetails: null, // Allow null for the case when not provided
    date: "", // Default to current date, will be validated later
    needPrasad: null, // Use null to indicate not yet selected
  },
  paymentInfo: {},
  loading: false,
  error: null,
  paymentResponseDialogOpen: false, // Initialize dialog state
  paymentAccessKey: null, // Initialize payment access key
  paymentStatus: null,
  transactionId: null, // Initialize transaction ID
  ThalBhetHistory: [], // Initialize Thalbhet history as an empty array
  isRequiredPanCard: false, // Initialize PAN card requirement state
  thalbhetId: null, // Initialize Thalbhet ID
};

interface ThalbhetThunkArg {
  amount: number;
  member_id: number | null;
  date: string;
  is_family_member: boolean;
  name: string | null;
  village: string | null;
  nimit: string;
  nimit_note: string | null;
  is_prasad: boolean | null; // Use boolean or null to handle the toggle state
  prasad: number | null; // Use number or null to handle the case when not provided
  thal_place: string;
  payment_type: string;
  pan_card?: string; // Optional field for PAN card
  kundal_amount: number | null; // Amount for Kundal
  vadodara_amount: number | null; // Amount for Vadodara
  account_holder_name?: string; // Account holder name for payment
  account_phone_number?: string; // Phone number for payment
  account_holder_city?: string; // City of the account holder
}

const token = getCookie(AUTH_COOKIE_TOKEN);
setLatestOtpToken(token ?? "");
const smkApi = createSmkApi(); // Create a Axios instance

// create thalbhetThunk for submit data
export const thalbhetThunk = createAsyncThunk(
  "thalbhet/submit",
  async ({ token, payload }: { token: string; payload: ThalbhetThunkArg }, { rejectWithValue }) => {
    try {
      const response = await smkApi.post(API_PATH.THALBHET, payload);
      return response.data;
    } catch (error) {
      const err = error as AxiosError<{ message: string }>;
      const message = err.response?.data?.message || err.message || "Unknown error";
      return rejectWithValue(message);
    }
  }
);

//verify payment thunk
export const verifyPaymentThunk = createAsyncThunk(
  "thalbhet/verifyPayment",
  async ({ token, payload }: { token: string; payload: any }, { rejectWithValue }) => {
    try {
      const response = await smkApi.post(API_PATH.VERIFY_PAYMENT, { response_json: payload });
      return response.data;
    } catch (error) {
      const err = error as AxiosError<{ message: string }>;
      const message = err.response?.data?.message || err.message || "Unknown error";
      return rejectWithValue(message);
    }
  }
);

//download pavati thunk
export const downloadPavatiThunk = createAsyncThunk(
  "thalbhet/downloadPavati",
  async ({ token, thalbhetID }: { token: string; thalbhetID: number }, { rejectWithValue, getState }) => {
    const state = getState();
    try {
      const response = await smkApi.get(`${API_PATH.DOWNLOAD_PAVATI}/${thalbhetID}`, {
        responseType: "blob", // ⬅️ Important
      });

      const contentDisposition = response.headers["content-disposition"];
      const filenameRegex = /filename=([^;]*)/;
      const match = contentDisposition.match(filenameRegex);
      const filename = match ? match[1].trim() : `pavati_${(state as any)?.transactionId}.pdf`; // Fallback filename if not found

      fileSaver.saveAs(new Blob([response.data]), filename);
      return response.data;
    } catch (error) {
      const err = error as AxiosError<{ message: string }>;
      const message = err.response?.data?.message || err.message || "Unknown error";
      return rejectWithValue(message);
    }
  }
);

const thalbhetSlice = createSlice({
  name: "thalbhet",
  initialState,
  reducers: {
    // Step management actions
    nextStep: (state) => {
      if (state.currentStep < state.totalSteps) {
        state.currentStep += 1;
      }
    },
    prevStep: (state) => {
      if (state.currentStep > 1) {
        state.currentStep -= 1;
      }
    },
    setStep: (state, action: PayloadAction<number>) => {
      const step = action.payload;
      if (step >= 1 && step <= state.totalSteps) {
        state.currentStep = step;
      }
    },
    resetStepper: (state) => {
      state.currentStep = 1;
    },

    // Basic Info actions
    updateBasicInfo: (state, action: PayloadAction<Partial<ThalBhetState["basicInfo"]>>) => {
      state.basicInfo = { ...state.basicInfo, ...action.payload };
      state.currentStep = 2;
    },

    // Rasoi Info actions
    setIsPrasadNeeded: (state, action: PayloadAction<boolean | null>) => {
      state.rasoiInfo.needPrasad = action.payload;
    },

    updateRasoiInfo: (state, action: PayloadAction<Partial<ThalBhetState["rasoiInfo"]>>) => {
      state.rasoiInfo = { ...state.rasoiInfo, ...action.payload };
      state.currentStep = 3; // Move to payment step after updating rasoi info
    },

    // Reset all form data
    resetForm: (state) => {
      state.currentStep = 1;
      state.basicInfo = initialState.basicInfo;
      state.rasoiInfo = initialState.rasoiInfo;
      state.paymentInfo = initialState.paymentInfo;
    },

    // Payment Response Dialog actions
    setPaymentResponseDialogOpen: (state) => {
      state.paymentResponseDialogOpen = true;
    },

    setPaymentResponseDialogClose: (state) => {
      state.paymentResponseDialogOpen = false;
    },

    // set payment status
    setPaymentStatus: (state, action: PayloadAction<paymentStatusType>) => {
      state.paymentStatus = action.payload;
      state.currentStep = action.payload === null ? 1 : state.currentStep; // Reset to step 1 if payment status is null
    },

    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload; // Set error message
      state.loading = false; // Stop loading state
    },

    setThalbhetHistory: (state, action: PayloadAction<ThalbhetHistoryData[]>) => {
      state.ThalBhetHistory = action.payload; // Set Thalbhet history
    },

    setThalbhetId: (state, action: PayloadAction<number | null>) => {
      state.thalbhetId = action.payload; // Set Thalbhet ID
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(thalbhetThunk.pending, (state) => {
        state.loading = true; // Optionally handle loading state
        state.error = null; // Reset error state
      })
      .addCase(thalbhetThunk.fulfilled, (state, action) => {
        state.loading = false;
        state.paymentAccessKey = action.payload.data?.data || null; // Capture payment access key
        state.isRequiredPanCard = false; // Reset PAN card requirement state
      })
      .addCase(thalbhetThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string; // Handle error state
        if (action.payload === "Provide PAN card for transactions above ₹40,000") {
          state.isRequiredPanCard = true;
        }
      });

    builder
      .addCase(verifyPaymentThunk.pending, (state) => {
        state.loading = true; // Optionally handle loading state
        state.error = null; // Reset error state
      })
      .addCase(verifyPaymentThunk.fulfilled, (state, action) => {
        state.loading = false;
        state.transactionId = action.payload.data?.transaction_id || null; // Capture transaction ID
        state.paymentStatus = action.payload.data?.status || "failed"; // Set payment status based on response
        state.thalbhetId = action.payload.data?.thalbhet_id || null; // Capture Thalbhet ID
      })
      .addCase(verifyPaymentThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string; // Handle error state
        state.paymentStatus = "failed"; // Set payment status to failed on error
      });

    builder
      .addCase(downloadPavatiThunk.pending, (state) => {
        state.loading = true; // Optionally handle loading state
        state.error = null; // Reset error state
      })
      .addCase(downloadPavatiThunk.fulfilled, (state) => {
        state.loading = false;
        // Handle successful download response if needed
      })
      .addCase(downloadPavatiThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string; // Handle error state
      });
  },
});

export const {
  // Step actions
  nextStep,
  prevStep,
  setStep,
  resetStepper,

  // Basic Info actions
  updateBasicInfo,

  // Rasoi Info actions
  updateRasoiInfo,
  setIsPrasadNeeded,

  // Form reset
  resetForm,

  // Payment Response Dialog actions
  setPaymentResponseDialogOpen,
  setPaymentResponseDialogClose,

  // Payment status
  setPaymentStatus,

  // Error handling
  setError,

  setThalbhetHistory,
  setThalbhetId,
} = thalbhetSlice.actions;

export default thalbhetSlice.reducer;
